import { useEffect, useState } from 'react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import TabelaPrecosPosto from '../components/TabelaPrecosPosto'

// Cadastro de Postos de Abastecimento (GeneXus TPRACAS → frota.pracas), em Combustível → Parâmetros.
interface Posto {
  pracod: string; pranom: string; praraz: string; praend: string; pracid: string; estcod: string
  pracgc: string; pracred: string; practf: string; practo: string; pratel: string; prapon: string; pravel: string
  pradmo?: string | null; pon_des?: string | null; ult_data?: string | null; ult_valor?: number
}
const NOVO: Posto = { pracod: '', pranom: '', praraz: '', praend: '', pracid: '', estcod: '', pracgc: '', pracred: 'S', practf: '', practo: '', pratel: '', prapon: '', pravel: '' }
const UFS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO']

const soDigitos = (v: string) => (v || '').replace(/\D/g, '')
const semZero = (v: string) => (v === '0' ? '' : v)
function formatarCnpj(v: string) {
  const d = soDigitos(v)
  if (!d || d === '0') return ''
  return d.padStart(14, '0').replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
}
const dataBR = (d?: string | null) => (d ? d.split('-').reverse().join('/') : '  /  /    ')

function Campo({ label, width, children }: { label: string; width?: number | string; children: React.ReactNode }) {
  return (
    <div className="field-cad" style={{ width }}>
      <label style={{ display: 'block', minHeight: '1.1rem', lineHeight: 1.2 }}>{label}</label>
      {children}
    </div>
  )
}

const secao: React.CSSProperties = {
  background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8', borderRadius: '0.5rem', padding: '0.55rem 0.85rem',
  marginBottom: '0.55rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.9rem', alignItems: 'flex-end',
}

export default function CadastroPostos() {
  const [lista, setLista] = useState<Posto[]>([])
  const [busca, setBusca] = useState('')
  const [carregando, setCarregando] = useState(false)
  const [selecionado, setSelecionado] = useState<string | null>(null) // '' = novo
  const [form, setForm] = useState<Posto | null>(null)
  const [combustiveis, setCombustiveis] = useState<string[]>([])
  const [cnpjDigitado, setCnpjDigitado] = useState('')
  const [cnpjFocado, setCnpjFocado] = useState(false)
  const [aba, setAba] = useState<'dados' | 'precos'>('dados')
  const [salvando, setSalvando] = useState(false)
  const [msg, setMsg] = useState<{ texto: string; ok: boolean } | null>(null)

  async function carregarLista(termo: string) {
    setCarregando(true)
    try { setLista((await api.get('/posto-cadastro', { params: termo ? { busca: termo } : {} })).data) }
    finally { setCarregando(false) }
  }
  useEffect(() => { api.get('/combustivel-cadastro').then(r => setCombustiveis(r.data.map((c: any) => c.comcod))).catch(() => {}) }, [])
  useEffect(() => { const t = setTimeout(() => carregarLista(busca), 300); return () => clearTimeout(t) }, [busca])

  async function selecionar(cod: string) {
    setMsg(null)
    const r = await api.get(`/posto-cadastro/${encodeURIComponent(cod)}`)
    setSelecionado(cod); setForm(r.data); setCnpjDigitado(semZero(r.data.pracgc))
  }
  function novo() { setSelecionado(''); setAba('dados'); setForm({ ...NOVO }); setCnpjDigitado(''); setMsg(null) }
  const set = <K extends keyof Posto>(k: K, v: Posto[K]) => setForm(f => f && { ...f, [k]: v })

  const deposito = !!form?.pracod.toUpperCase().startsWith('DP')

  // PBUSPOS: no posto novo, puxa os dados do cadastro de clientes pelo CNPJ.
  async function buscarPorCnpj() {
    if (!form || selecionado || deposito) return
    const cnpj = soDigitos(cnpjDigitado)
    if (cnpj.length < 14) return
    try {
      const d = (await api.get(`/posto-cadastro/busca-cnpj/${cnpj}`)).data
      if (d) setForm(f => f && { ...f, ...Object.fromEntries(Object.entries(d).filter(([, v]) => v)) })
    } catch { /* sem cadastro, segue digitando */ }
  }

  async function salvar() {
    if (!form) return
    setSalvando(true); setMsg(null)
    try {
      const corpo = { ...form, pracgc: soDigitos(cnpjDigitado), practf: soDigitos(form.practf) }
      if (selecionado) await api.put(`/posto-cadastro/${encodeURIComponent(selecionado)}`, corpo)
      else await api.post('/posto-cadastro', corpo)
      const cod = selecionado || form.pracod.trim().toUpperCase()
      await carregarLista(busca); await selecionar(cod)
      setMsg({ texto: selecionado ? 'Alterações salvas.' : 'Posto cadastrado.', ok: true })
    } catch (e: any) { setMsg({ texto: e?.response?.data?.error || 'Erro ao salvar.', ok: false }) }
    finally { setSalvando(false) }
  }

  async function excluir() {
    if (!selecionado || !window.confirm(`Excluir o posto ${selecionado}?`)) return
    setSalvando(true); setMsg(null)
    try {
      await api.delete(`/posto-cadastro/${encodeURIComponent(selecionado)}`)
      setSelecionado(null); setForm(null); await carregarLista(busca)
    } catch (e: any) { setMsg({ texto: e?.response?.data?.error || 'Erro ao excluir.', ok: false }) }
    finally { setSalvando(false) }
  }

  return (
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
      <div className="card" style={{ width: 300, flexShrink: 0 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.9rem', color: '#1a6ea8', paddingBottom: '0.6rem', borderBottom: '2px solid #eff6ff' }}>Postos de Abastecimento</h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem', alignItems: 'center' }}>
          <div className="field" style={{ flex: 1 }}>
            <input type="text" placeholder="Código, nome ou CNPJ..." value={busca} onChange={e => setBusca(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={novo}>+ Novo</button>
        </div>
        {carregando && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Carregando...</p>}
        {!carregando && lista.length === 0 && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nenhum posto encontrado.</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
          {lista.map(p => {
            const ativo = selecionado === p.pracod
            return (
              <button key={p.pracod} onClick={() => selecionar(p.pracod)} style={{
                textAlign: 'left', border: '1px solid #e5e7eb', borderLeft: '4px solid #1a6ea8', borderRadius: 6, padding: '8px 10px', cursor: 'pointer',
                background: ativo ? '#eff6ff' : '#f8fafc', borderColor: ativo ? '#1a6ea8' : '#e5e7eb',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{p.pracod}</span>
                  <span style={{ fontSize: '0.8rem', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.pranom}</span>
                  {p.pracod.startsWith('DP') && <span style={{ fontSize: '0.66rem', fontWeight: 700, color: '#1a6ea8', background: '#dbeafe', borderRadius: 4, padding: '0.1rem 0.4rem' }}>Depósito</span>}
                  {p.pracred === 'N' && <span style={{ fontSize: '0.66rem', fontWeight: 700, color: '#fff', background: '#9ca3af', borderRadius: 4, padding: '0.1rem 0.4rem' }}>Não cred.</span>}
                </div>
                <div style={{ fontSize: '0.76rem', color: '#374151', marginTop: 3 }}>{[p.pracid, p.estcod].filter(Boolean).join(' / ') || '—'}</div>
              </button>
            )
          })}
        </div>
      </div>

      <div className="card" style={{ flex: 1, minWidth: 0 }}>
        {selecionado === null && <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Selecione um posto na lista ao lado, ou clique em "+ Novo".</p>}
        {selecionado !== null && form && (
          <>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a6ea8', marginBottom: '0.6rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
              {selecionado ? `Posto ${selecionado} — ${form.pranom}` : 'Novo Posto'}
              {form.pradmo && <span style={{ marginLeft: 12, fontSize: '0.72rem', fontWeight: 500, color: '#94a3b8' }}>última alteração em {dataBR(form.pradmo)}</span>}
            </h3>
            {selecionado && (
              <div style={{ display: 'flex', gap: 4, marginBottom: '0.7rem', borderBottom: '1px solid #e5e7eb' }}>
                {([['dados', 'Dados do Posto'], ['precos', 'Tabela de Preços']] as const).map(([k, t]) => (
                  <button key={k} onClick={() => { if (k === 'dados' && aba !== 'dados') selecionar(selecionado); setAba(k) }} style={{
                    border: 'none', background: 'none', padding: '6px 14px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, marginBottom: -1,
                    color: aba === k ? '#1a6ea8' : '#6b7280', borderBottom: aba === k ? '2px solid #1a6ea8' : '2px solid transparent',
                  }}>{t}</button>
                ))}
              </div>
            )}
            {selecionado && aba === 'precos' ? <TabelaPrecosPosto pracod={selecionado} /> : <>
            <div style={secao}>
              <Campo label="Código" width={70}>
                <input type="text" maxLength={4} value={form.pracod} disabled={!!selecionado} onChange={e => set('pracod', e.target.value.toUpperCase())} style={{ width: '100%' }} />
              </Campo>
              <Campo label="CNPJ" width={170}>
                <input type="text" inputMode="numeric" maxLength={18} value={cnpjFocado ? cnpjDigitado : formatarCnpj(cnpjDigitado)} onFocus={() => setCnpjFocado(true)}
                  onChange={e => setCnpjDigitado(soDigitos(e.target.value).slice(0, 14))} onBlur={() => { setCnpjFocado(false); buscarPorCnpj() }}
                  title={!selecionado && !deposito ? 'Ao sair do campo, busca os dados no cadastro de clientes' : undefined} style={{ width: '100%' }} />
              </Campo>
              <Campo label="Nome" width={200}>
                <input type="text" maxLength={20} value={form.pranom} onChange={e => set('pranom', e.target.value.toUpperCase())} style={{ width: '100%' }} />
              </Campo>
              <Campo label="Credenciado" width={90}>
                <select value={form.pracred} onChange={e => set('pracred', e.target.value)} style={{ width: '100%' }}>
                  <option value="S">Sim</option>
                  <option value="N">Não</option>
                </select>
              </Campo>
            </div>
            <div style={secao}>
              <Campo label="Razão Social" width={360}>
                <input type="text" maxLength={40} value={form.praraz} onChange={e => set('praraz', e.target.value.toUpperCase())} style={{ width: '100%' }} />
              </Campo>
              <Campo label="Endereço" width={360}>
                <input type="text" maxLength={40} value={form.praend} onChange={e => set('praend', e.target.value.toUpperCase())} style={{ width: '100%' }} />
              </Campo>
              <Campo label="Cidade" width={240}>
                <input type="text" maxLength={25} value={form.pracid} onChange={e => set('pracid', e.target.value.toUpperCase())} style={{ width: '100%' }} />
              </Campo>
              <Campo label="Estado" width={75}>
                <select value={form.estcod} onChange={e => set('estcod', e.target.value)} style={{ width: '100%' }}>
                  <option value="">—</option>
                  {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </Campo>
            </div>
            <div style={secao}>
              <Campo label="Código CTF" width={120}>
                <input type="text" inputMode="numeric" maxLength={10} value={semZero(form.practf)} onChange={e => set('practf', soDigitos(e.target.value))} style={{ width: '100%' }} />
              </Campo>
              <Campo label="Contato" width={200}>
                <input type="text" maxLength={20} value={form.practo} onChange={e => set('practo', e.target.value.toUpperCase())} style={{ width: '100%' }} />
              </Campo>
              <Campo label="Telefone" width={150}>
                <input type="text" maxLength={14} value={form.pratel} onChange={e => set('pratel', e.target.value)} style={{ width: '100%' }} />
              </Campo>
            </div>
            <div style={{ ...secao, opacity: deposito ? 1 : 0.6 }}>
              <Campo label="Pto Estoque" width={300}>
                <LookupSelect endpoint="/erp/oedcad/lista" codKey="oed_cod" descKey="oed_des" tagKey="oed_cod"
                  codValue={form.prapon} descValue={form.pon_des || form.prapon} disabled={!deposito}
                  onSelect={(cod, des) => setForm(f => f && { ...f, prapon: cod, pon_des: des })} placeholder="Nenhum" />
              </Campo>
              <Campo label="Código Combustível" width={140}>
                <select value={form.pravel} disabled={!deposito} onChange={e => set('pravel', e.target.value)} style={{ width: '100%' }}>
                  <option value="">—</option>
                  {combustiveis.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Campo>
              {!deposito && <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>(só para depósitos — código começando com DP)</span>}
            </div>
            {selecionado && (
              <div style={{ ...secao, background: '#f8fafc', borderLeftColor: '#94a3b8', gap: '0.4rem 1.4rem', fontSize: '0.82rem', color: '#374151' }}>
                <span style={{ fontWeight: 700 }}>Preço vigente:</span>
                <span>Último reajuste: <b>{dataBR(form.ult_data)}</b></span>
                <span>Diesel S-500: <b>{(form.ult_valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</b></span>
                <button className="btn btn-sm" onClick={() => setAba('precos')}>Ver tabela de preços</button>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="btn btn-primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : selecionado ? 'Salvar alterações' : 'Cadastrar'}</button>
              {selecionado && <button className="btn" onClick={excluir} disabled={salvando} style={{ color: '#b91c1c' }}>Excluir</button>}
              {msg && (
                <span style={{ fontSize: '0.8rem', fontWeight: 600, padding: '0.35rem 0.8rem', borderRadius: 6, background: msg.ok ? '#d1fae5' : '#fee2e2', color: msg.ok ? '#065f46' : '#991b1b' }}>{msg.texto}</span>
              )}
            </div>
            </>}
          </>
        )}
      </div>
    </div>
  )
}
